import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ShieldAlert } from 'lucide-react';
import useApi from '../api';
import toastConfig from '../config/Toast';
import { Switch } from '@/components/ui/switch';

// organizations.settings["session_moderation"], served by
// /users/me/organization/moderation (SESSION_MODERATION_PLAN.md §1.1).
// The policy itself is not configurable: see the description below.
type ModerationSettings = {
  enabled: boolean;
  notify: boolean;
  ban_hours: number;
};

export default function SessionModerationSettings() {
  const { get, put } = useApi();
  const [settings, setSettings] = useState<ModerationSettings | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await get('/users/me/organization/moderation');
        if (!cancelled) setSettings(response.data);
      } catch (error) {
        console.error('Error fetching session moderation settings:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = async (patch: Partial<Pick<ModerationSettings, 'enabled' | 'notify'>>, successMessage: string) => {
    setSaving(true);
    try {
      const response = await put('/users/me/organization/moderation', patch);
      setSettings(response.data);
      toast.success(successMessage, toastConfig);
    } catch (error: any) {
      console.error('Error updating session moderation settings:', error);
      toast.error(
        typeof error?.detail === 'string' ? error.detail : 'Failed to update session moderation',
        toastConfig,
      );
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;

  const hours = settings.ban_hours;

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold">Session moderation</h3>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          Aimdoc watches for visitors who attack or probe your agent instead of asking about your product. Each
          flagged session is reviewed before anything is done. A visitor found acting in bad faith is banned from
          the chat for {hours} hours. If a second visitor from the same IP address is banned within {hours} hours,
          that IP address is banned too. Verified users are never banned.
        </p>
      </div>
      <div className="divide-y overflow-hidden rounded-lg border bg-card">
        <div className="flex items-center justify-between gap-4 p-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-muted/30 text-muted-foreground">
              <ShieldAlert className="h-3.5 w-3.5" />
            </div>
            <div className="min-w-0">
              <span className="text-sm font-medium">Moderate sessions</span>
              <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
                {settings.enabled
                  ? 'On. Conversations on your website are checked.'
                  : 'Off. Conversations are not checked and nobody is banned automatically.'}
              </p>
            </div>
          </div>
          <Switch
            className="shrink-0"
            checked={settings.enabled}
            disabled={saving}
            onCheckedChange={(checked) =>
              update({ enabled: checked }, checked ? 'Session moderation turned on' : 'Session moderation turned off')
            }
          />
        </div>

        <div className="flex items-center justify-between gap-4 p-4">
          <div className="min-w-0">
            <span className="text-sm font-medium">Alert the team</span>
            <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
              Send a Session Flagged alert when a review finds bad faith. Each teammate and Slack channel still
              chooses whether to receive it.
            </p>
          </div>
          <Switch
            className="shrink-0"
            checked={settings.notify}
            disabled={saving || !settings.enabled}
            onCheckedChange={(checked) =>
              update({ notify: checked }, checked ? 'Session flagged alerts on' : 'Session flagged alerts off')
            }
          />
        </div>
      </div>
    </section>
  );
}
